/**
 * What is actually deployed across the estate, by resource type.
 *
 * A flat census rather than a chart: the useful reading is "41 key vaults, 57
 * storage accounts, 86 metric alerts", and a bar chart of 60 types would say
 * less than the numbers themselves.
 */

import { Badge, Typography } from "@norican/react";

import type { ResourceTypeCount } from "@/lib/api";

import { PanelCard } from "./panel-card";
import { formatCount } from "./status";

interface ResourceCensusProps {
  resourceTypes: ResourceTypeCount[];
  /** Distinct types in the estate, including the tail this panel does not list. */
  totalTypes: number;
}

export function ResourceCensus({ resourceTypes, totalTypes }: ResourceCensusProps) {
  if (resourceTypes.length === 0) {
    return null;
  }

  return (
    <PanelCard
      title="Resource census"
      caption={`Top ${resourceTypes.length} of ${totalTypes} resource types in use`}
    >
      <div className="flex flex-wrap gap-2">
        {resourceTypes.map((entry) => (
          <Badge key={entry.type} tone="neutral" variant="outline" size="sm">
            <span className="text-fg font-medium tabular-nums">{formatCount(entry.resources)}</span>
            {" "}
            {entry.kind}
          </Badge>
        ))}
      </div>
      <Typography variant="micro" tone="muted" as="p">
        Counted with one Azure Resource Graph query across every readable subscription.
      </Typography>
    </PanelCard>
  );
}
